import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Modal, 
  Input, 
  List, 
  Typography, 
  Empty, 
  Spin, 
  Tag, 
  Space, 
  Divider,
  Badge,
  theme
} from 'antd';
import { SearchOutlined, EnterOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useGlobalSearch, SearchResult } from '../../hooks/useGlobalSearch';
import { useDebouncedCallback } from 'use-debounce';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('GlobalSearchModal');

// Custom styles for the scrollable results area
const scrollbarStyles = `
  .global-search-results::-webkit-scrollbar {
    width: 6px;
  }
  .global-search-results::-webkit-scrollbar-track {
    background: transparent;
  }
  .global-search-results::-webkit-scrollbar-thumb {
    background: #bfbfbf;
    border-radius: 3px;
  }
  .global-search-results::-webkit-scrollbar-thumb:hover {
    background: #999;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = scrollbarStyles;
  if (!document.head.querySelector('style[data-component="global-search"]')) {
    styleElement.setAttribute('data-component', 'global-search');
    document.head.appendChild(styleElement);
  }
}

const { Text } = Typography;

interface GlobalSearchModalProps {
  open: boolean;
  onClose: () => void;
}

const GlobalSearchModal: React.FC<GlobalSearchModalProps> = ({ open, onClose }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { search } = useGlobalSearch();
  const inputRef = useRef<any>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('recent-searches') || '[]');
    } catch {
      return [];
    }
  });

  // Debounced search function
  const debouncedSearch = useDebouncedCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      logger.debug('Starting global search:', searchQuery);
      
      const searchResults = await search(searchQuery, {
        limit: 50,
        includeClientData: true,
        includeServerData: true
      });
      
      setResults(searchResults);
      setSelectedIndex(0);
      logger.debug('Search completed:', { query: searchQuery, resultCount: searchResults.length });
    } catch (error) {
      logger.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, 300);

  // Handle query change
  const handleQueryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    debouncedSearch(newQuery);
  }, [debouncedSearch]);

  // Save search to recent searches
  const saveToRecentSearches = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) return;
    
    const updated = [
      searchQuery,
      ...recentSearches.filter(s => s !== searchQuery)
    ].slice(0, 10);
    
    setRecentSearches(updated);
    localStorage.setItem('recent-searches', JSON.stringify(updated));
  }, [recentSearches]);

  // Handle result selection
  const handleResultSelect = useCallback((result: SearchResult) => {
    logger.debug('Selected search result:', result);
    saveToRecentSearches(query);
    result.action();
    onClose();
  }, [query, saveToRecentSearches, onClose]);

  // Handle recent search selection
  const handleRecentSearchSelect = useCallback((recentQuery: string) => {
    setQuery(recentQuery);
    debouncedSearch(recentQuery);
  }, [debouncedSearch]);

  // Scroll selected item into view
  const scrollSelectedIntoView = useCallback((index: number) => {
    if (!resultsContainerRef.current) return;
    
    const container = resultsContainerRef.current;
    const items = container.querySelectorAll('[data-result-index]');
    const selectedItem = items[index] as HTMLElement;
    
    if (selectedItem) {
      const containerRect = container.getBoundingClientRect();
      const itemRect = selectedItem.getBoundingClientRect();
      
      if (itemRect.bottom > containerRect.bottom) {
        // Scroll down
        container.scrollTop += itemRect.bottom - containerRect.bottom + 10;
      } else if (itemRect.top < containerRect.top) {
        // Scroll up
        container.scrollTop -= containerRect.top - itemRect.top + 10;
      }
    }
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => {
            const newIndex = prev < results.length - 1 ? prev + 1 : prev;
            setTimeout(() => scrollSelectedIntoView(newIndex), 0);
            return newIndex;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => {
            const newIndex = prev > 0 ? prev - 1 : prev;
            setTimeout(() => scrollSelectedIntoView(newIndex), 0);
            return newIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            handleResultSelect(results[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, results, selectedIndex, handleResultSelect, onClose, scrollSelectedIntoView]);

  // Note: Global keyboard shortcut (Cmd/Ctrl + K) is handled by App.tsx to avoid conflicts

  // Reset state when modal opens/closes and handle auto-focus
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setLoading(false);
      
      // Auto-focus the input with a small delay to ensure modal is fully rendered
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  }, [open]);

  // Group results by category
  const groupedResults = useMemo(() => {
    const groups: Record<string, SearchResult[]> = {};
    results.forEach(result => {
      if (!groups[result.category]) {
        groups[result.category] = [];
      }
      groups[result.category].push(result);
    });
    return groups;
  }, [results]);

  const getCategoryIcon = (category: string) => {
    const icons = {
      files: '📁',
      metadata: '🗃️',
      steps: '⚙️',
      pipelines: '🔄',
      documentation: '📖'
    };
    return icons[category as keyof typeof icons] || '📄';
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      files: 'blue',
      metadata: 'green',
      steps: 'orange',
      pipelines: 'purple',
      documentation: 'cyan'
    };
    return colors[category as keyof typeof colors] || 'default';
  };

  const getMatchTypeIcon = (matchType?: string) => {
    const icons = {
      exact: '🎯',
      prefix: '▶️',
      contains: '🔍',
      fuzzy: '✨'
    };
    return icons[matchType as keyof typeof icons] || '🔍';
  };

  const getMatchTypeColor = (matchType?: string) => {
    const colors = {
      exact: 'green',
      prefix: 'blue',
      contains: 'orange',
      fuzzy: 'purple'
    };
    return colors[matchType as keyof typeof colors] || 'default';
  };

  const showRecentSearches = !query && recentSearches.length > 0;
  const showEmptyState = !loading && query && results.length === 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
      centered
      className="global-search-modal"
      style={{ top: 80 }}
      styles={{
        body: { padding: 0 },
        content: { borderRadius: '12px', overflow: 'hidden' }
      }}
    >
      {/* Search Input */}
      <div style={{ padding: '20px 20px 0', borderBottom: `1px solid ${token.colorBorder}` }}>
        <Input
          ref={inputRef}
          size="large"
          placeholder={t('search.placeholder')}
          prefix={<SearchOutlined style={{ color: token.colorTextSecondary }} />}
          value={query}
          onChange={handleQueryChange}
          autoFocus
          style={{
            fontSize: '16px',
            border: 'none',
            boxShadow: 'none'
          }}
        />
      </div>

      {/* Results Area */}
      <div 
        ref={resultsContainerRef}
        style={{ 
          maxHeight: '500px', 
          minHeight: '200px', 
          overflowY: 'auto',
          overflowX: 'hidden',
          // Custom scrollbar styles
          scrollbarWidth: 'thin',
          scrollbarColor: '#bfbfbf transparent'
        }}
        className="global-search-results"
      >
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px', color: token.colorTextSecondary }}>
              {t('search.loadingResults')}
            </div>
          </div>
        )}

        {showRecentSearches && (
          <div style={{ padding: '16px' }}>
            <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {t('search.recentSearches')}
            </Text>
            <div style={{ marginTop: '8px' }}>
              {recentSearches.map((recentQuery, index) => (
                <Tag
                  key={index}
                  style={{ 
                    margin: '4px 4px 4px 0',
                    cursor: 'pointer',
                    border: `1px solid ${token.colorBorder}`
                  }}
                  onClick={() => handleRecentSearchSelect(recentQuery)}
                >
                  {recentQuery}
                </Tag>
              ))}
            </div>
          </div>
        )}

        {showEmptyState && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Empty
              description={t('search.noResults')}
              style={{ marginBottom: 0 }}
            />
          </div>
        )}

        {!loading && results.length > 0 && (
          <div style={{ padding: '8px 0' }}>
            {Object.entries(groupedResults).map(([category, categoryResults], groupIndex) => (
              <div key={category}>
                {groupIndex > 0 && <Divider style={{ margin: '8px 0' }} />}
                
                {/* Category Header */}
                <div style={{ 
                  padding: '8px 20px 4px',
                  position: 'sticky',
                  top: 0,
                  background: token.colorBgLayout,
                  borderBottom: `1px solid ${token.colorBorder}`,
                  zIndex: 10,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <Space>
                    <span>{getCategoryIcon(category)}</span>
                    <Text strong style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t(`search.categories.${category}`)}
                    </Text>
                    <Badge count={categoryResults.length} color={getCategoryColor(category)} />
                  </Space>
                </div>

                {/* Category Results */}
                <List
                  size="small"
                  dataSource={categoryResults}
                  renderItem={(result, index) => {
                    const globalIndex = results.indexOf(result);
                    const isSelected = globalIndex === selectedIndex;
                    
                    return (
                      <List.Item
                        data-result-index={globalIndex}
                        style={{
                          padding: '12px 20px',
                          cursor: 'pointer',
                          backgroundColor: isSelected ? token.colorPrimaryBg : 'transparent',
                          borderLeft: isSelected ? `3px solid ${token.colorPrimary}` : '3px solid transparent',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => handleResultSelect(result)}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                      >
                        <List.Item.Meta
                          avatar={result.icon}
                          title={
                            <Space>
                              <Text strong>{result.title}</Text>
                              <Tag color={getCategoryColor(result.category)}>
                                {result.type}
                              </Tag>
                              {result.matchType && (
                                <Tag 
                                  color={getMatchTypeColor(result.matchType)} 
                                  title={`Match type: ${result.matchType}${result.relevance ? ` (relevance: ${Math.round(result.relevance)}%)` : ''}`}
                                >
                                  <Space size={2}>
                                    <span>{getMatchTypeIcon(result.matchType)}</span>
                                    <span style={{ fontSize: '10px' }}>{result.matchType}</span>
                                  </Space>
                                </Tag>
                              )}
                            </Space>
                          }
                          description={
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {result.description}
                              {result.path && (
                                <span style={{ marginLeft: '8px', fontFamily: 'monospace' }}>
                                  {result.path}
                                </span>
                              )}
                              {result.relevance && result.relevance < 70 && (
                                <span style={{ marginLeft: '8px', color: token.colorTextTertiary, fontSize: '10px' }}>
                                  ~{Math.round(result.relevance)}% match
                                </span>
                              )}
                            </Text>
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer with shortcuts */}
      {!loading && results.length > 0 && (
        <div style={{ 
          padding: '12px 20px',
          borderTop: `1px solid ${token.colorBorder}`,
          background: token.colorBgLayout
        }}>
          <Space size="large">
            <Space size="small">
              <EnterOutlined style={{ fontSize: '10px', color: token.colorTextSecondary }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>to select</Text>
            </Space>
            <Space size="small">
              <ArrowUpOutlined style={{ fontSize: '10px', color: token.colorTextSecondary }} />
              <ArrowDownOutlined style={{ fontSize: '10px', color: token.colorTextSecondary }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>to navigate</Text>
            </Space>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('search.resultsCount', { count: results.length })}
            </Text>
          </Space>
        </div>
      )}
    </Modal>
  );
};

export default GlobalSearchModal; 